import { useViewport } from 'contexts/viewport';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Paper,
    RichTextArea,
    RubricTable,
    Stack,
    Text,
    styled,
} from 'shell/components';
import { BASELINE_WORD_COUNT_LIMIT } from 'utils/constants';
import { getHTMLWordCount } from 'utils/helper';

const ViewBaselineDialog = ({ open, rubric, baseline, onClose }) => {
    const { device } = useViewport();

    if (!baseline) return null;

    return (
        <Dialog
            sx={{
                '& .MuiDialog-paper': {
                    width: '100%',
                    height: '100%',
                },
            }}
            open={open}
            scroll={'paper'}
            onClose={onClose}
        >
            <DialogTitle>
                <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                    <Text variant={'h6'} weight={'medium'}>
                        Assignment Baseline
                    </Text>
                    <IconButton kind={'exit'} size={'medium'} variant={'square'} onClick={onClose} />
                </Stack>
            </DialogTitle>
            <Divider />
            <CustomDialogContent device={device}>
                <CustomStack
                    direction={device === 'desktop' ? 'row' : 'column'}
                    device={device}
                    spacing={device === 'desktop' ? 0 : 5}
                >
                    <InputStack device={device} spacing={5}>
                        <Text>
                            Please provide an example of a past{' '}
                            <Text component={'span'} weight={'semibold'}>
                                high-scoring
                            </Text>{' '}
                            assignment with the grades it received. This helps the AI to establish a baseline on the
                            rubric based on your assessment practices.
                        </Text>
                        <CustomRichTextArea
                            device={device}
                            defaultValue={baseline.assignment}
                            helperText={`${getHTMLWordCount(baseline.assignment)}/${BASELINE_WORD_COUNT_LIMIT} words`}
                            helperTextPosition={'end'}
                            readOnly
                            placeholder={'Please paste the assignment here'}
                        />
                    </InputStack>
                    <CustomPaper device={device}>
                        <RubricTable
                            type={'submit'}
                            disabled
                            data={rubric?.criteria}
                            form={baseline.criteria}
                            onChange={() => {}}
                        />
                    </CustomPaper>
                </CustomStack>
            </CustomDialogContent>
            <Divider />
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

const CustomDialogContent = styled(DialogContent)(({ theme, device }) => {
    return {
        background: theme.palette.ensightful.neutral[50],
        ...(device === 'desktop' && {
            padding: 0,
        }),
    };
});

const CustomStack = styled(Stack)(({ device }) => {
    return {
        ...(device === 'desktop' && {
            height: '100%',
            '& > *': {
                flex: 1,
            },
        }),
    };
});

const InputStack = styled(Stack)(({ device, theme }) => {
    return {
        ...(device === 'desktop' && {
            padding: theme.spacing(5),
        }),
    };
});

const CustomRichTextArea = styled(RichTextArea)(({ device }) => {
    return {
        ...(device === 'desktop'
            ? {
                  '& .MuiRichTextArea-root': {
                      minHeight: 'calc(100vh - 318px)',
                      maxHeight: 'calc(100vh - 318px)',
                  },
              }
            : {
                  '& .MuiRichTextArea-root': {
                      minHeight: '20rem',
                      maxHeight: '30rem',
                  },
              }),
    };
});

const CustomPaper = styled(Paper)(({ device, theme }) => {
    return {
        padding: theme.spacing(6),
        ...(device === 'desktop'
            ? {
                  borderRadius: '0',
                  overflow: 'auto',
              }
            : {
                  borderRadius: '12px 12px 0 0',
                  margin: theme.spacing(0, -6, -5),
              }),
    };
});

export default ViewBaselineDialog;
